import { useQuery } from "@tanstack/react-query";
import { useServices } from "@/services/ServiceProvider";
import { useAuthStore } from "@/stores/useAuthStore";
import { getActiveChainId } from "@/config/activeChain";
import { toReputationView, type ReputationView } from "@/lib/reputationView";
import { RpcChainMismatchError } from "@/lib/chainGuard";

/**
 * A user's on-chain reputation, read from `ReputationLedger`.
 *
 * Returns a `ReputationView` — a discriminated union — rather than the raw
 * query, so every surface switches on the same five states instead of each
 * re-deriving them from `isPending` / `isError` / `data`. That re-derivation is
 * what let the admin panel announce "Not registered on this network" while the
 * read was still in flight. See `lib/reputationView.ts`.
 *
 * Keyed by chain id as well as user. The chain is frozen for a page load and
 * switching networks reloads the app, so a cross-chain cache hit is not
 * reachable today — but the key states the dependency outright, so a future
 * in-place network switch cannot silently serve a mainnet score into a testnet
 * view. The admin Testnet/Mainnet selector is authoritative for this surface
 * like every other.
 */
export function useReputation(
  userId?: string,
  opts?: { enabled?: boolean },
): ReputationView {
  const { users } = useServices();
  const sessionUserId = useAuthStore((s) => s.session?.userId);
  const resolvedId = userId ?? sessionUserId;
  const chainId = getActiveChainId();

  const query = useQuery({
    queryKey: ["reputation", resolvedId, chainId],
    queryFn: () => users.getReputation(resolvedId!),
    enabled: !!resolvedId && (opts?.enabled ?? true),
    // A chain read is not free and the ledger only moves when a task is
    // approved, so refetching on every window focus buys nothing.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // One retry: a flaky public RPC is common, but retrying hard would leave
    // the card spinning long enough to read as broken. A misrouted RPC answers
    // the same way every time, so it gets none.
    retry: (failureCount, error) =>
      !(error instanceof RpcChainMismatchError) && failureCount < 1,
  });

  // A disabled query sits at isPending forever — it never fetches, so it never
  // settles. Reporting that as "loading" would spin a card that is never going
  // to fill, so an unresolvable user is "none": there is genuinely nothing to
  // read.
  if (!resolvedId || opts?.enabled === false) return { status: "none" };

  return toReputationView(query);
}
